import React, { useCallback, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { staticArrayAsProxy } from '../../../../../Interfaces/IAsProxy';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../../../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  getEmptySignerMember,
  getEmptySignerMemberData,
  IMemberSignerData,
} from '../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberSigner';
import { getEmptyNaturalPerson } from '../../../../../Interfaces/INaturalPerson';
import { staticTypeOfIdentificationArray } from '../../../../../Interfaces/ITypeOfIdentification';
import { DialogResponsive } from '../../../../core/DialogResponsive';
import { EditLegalClient } from '../../../../LegalClient/EditLegalClient';
import { TableLegalClient } from '../../../../LegalClient/TableLegalClient';
import { shallow, useLegalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';
import {
  present,
  removeLegalClientFromRelationships,
  updateOrCreateMember,
  updateOrCreateRelationship,
} from '../../generic/utilities';
import { SignatureCertificationLegalClientInformations } from '../components/SignatureCertificationLegalClientInformation';
import {
  getOrCreateSignatureCertificationRelationship,
  isSignatureCertificationMember,
} from '../utilities';

interface ISignatureCertificationEditLegalClientsProps {
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  handleNext: () => void;
  handleBack: () => void;
}

const isSignatureCeritificationMemberDataValid = (
  data: IMemberSignerData
): boolean => {
  const isValidTypeOfDocument = data.idTypeOfDocument > 0;
  const doesDocumentTypeRequireFreeText = staticTypeOfIdentificationArray.find(
    id => id.idTypeOfDocument === data.idTypeOfDocument
  )?.allowFreeText;
  const isValidDocumentData =
    isValidTypeOfDocument && doesDocumentTypeRequireFreeText
      ? !!data.textTypeOfDocument
      : true;

  const isProxy = data.asProxy;
  const doesProxyTypeRequireFreeText = staticArrayAsProxy.find(
    id => id.idAsProxy === data.idAsProxy
  )?.allowFreeText;
  const hasProxyTextIfRequired = doesProxyTypeRequireFreeText
    ? !!data.textAsProxy
    : true;
  const isValidProxyData = isProxy ? hasProxyTextIfRequired : true;

  return isValidDocumentData && isValidProxyData;
};

const useSignatureCertificationLegalClientStore = () => {
  const [legalClientArray, getLegalClient, putLegalClient, removeLegalClient] =
    useLegalClientStore(
      s => [
        s.legalClientArray,
        s.getLegalClient,
        s.putLegalClient,
        s.removeLegalClient,
      ],
      shallow
    );
  return {
    legalClientArray,
    getLegalClient,
    putLegalClient,
    removeLegalClient,
  };
};

export const SignatureCertificationEditLegalClients: React.FC<
  ISignatureCertificationEditLegalClientsProps
> = ({ handleNext, handleBack, relationshipArray, setRelationshipArray }) => {
  const {
    legalClientArray,
    putLegalClient,
    removeLegalClient,
    getLegalClient,
  } = useSignatureCertificationLegalClientStore();

  const methods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: {
      ...getEmptyLegalClient(legalClientArray),
      ...getEmptyNaturalPerson(),
      idLegalClient: 1,
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);

  const [editMemberData, setEditMemberData] = useState<IMemberSignerData>(
    getEmptySignerMemberData()
  );

  const signatureCertificationRelationship = useMemo(
    () => getOrCreateSignatureCertificationRelationship(relationshipArray),
    [relationshipArray]
  );
  const members = useMemo(
    () =>
      signatureCertificationRelationship.Members.filter(
        isSignatureCertificationMember
      ),
    [signatureCertificationRelationship]
  );
  const memberLegalClients = useMemo(
    () =>
      members
        .map(member => getLegalClient(member.idLegalClient))
        .filter(present),
    [members, legalClientArray]
  );

  const removeLegalClientFromStoreAndRelationships = useCallback(
    (legalClientToRemove: ILegalClient) => {
      removeLegalClient(legalClientToRemove.idLegalClient);

      const updatedRelationships = removeLegalClientFromRelationships(
        relationshipArray,
        legalClientToRemove.idLegalClient
      );
      setRelationshipArray(updatedRelationships);
    },
    [
      members,
      signatureCertificationRelationship,
      relationshipArray,
      setRelationshipArray,
    ]
  );

  const isOpen = isOpenAdd || isOpenEdit;

  const onClickNew = () => {
    methods.reset({
      ...getEmptyLegalClient(legalClientArray),
      ...getEmptyNaturalPerson(),
    });
    setEditMemberData(getEmptySignerMemberData());
    setIsOpenAdd(true);
  };

  const onClickEdit = (legalClient: ILegalClient) => {
    const existingMember = members.find(
      member => member.idLegalClient === legalClient.idLegalClient
    );

    if (!existingMember) {
      console.error(
        `No member found in onClickEdit for legal client with ID: ${legalClient.idLegalClient}`
      );
      return;
    }

    methods.reset({
      ...legalClient,
    } as IEditableLegalClient);
    setEditMemberData({
      ...existingMember.data,
    });
    setIsOpenEdit(true);
  };

  const onClose = () => {
    setIsOpenAdd(false);
    setIsOpenEdit(false);
  };

  const onSave = (legalClient: ILegalClient) => {
    putLegalClient(legalClient);

    const relationship =
      getOrCreateSignatureCertificationRelationship(relationshipArray);

    const newMember = getEmptySignerMember(
      legalClient,
      relationship.idRelationship,
      editMemberData
    );

    setRelationshipArray(
      updateOrCreateRelationship(relationshipArray, {
        ...relationship,
        Members: updateOrCreateMember(relationship.Members, newMember),
      })
    );
    onClose();
  };

  async function onSubmit() {
    const legalClientValid = methods.trigger();
    if (!legalClientValid) {
      return;
    }
    onSave(methods.getValues() as ILegalClient);
  }

  const { isValid } = methods.formState;

  return (
    <Box>
      <DialogResponsive
        open={isOpen}
        onClose={onClose}
        title={isOpenAdd ? 'Neue Person' : 'Person bearbeiten'}
        maxWidth='lg'
      >
        <FormProvider {...methods}>
          <form id='edit-legal-client-form' onSubmit={onSubmit} noValidate>
            <EditLegalClient
              disableCompanyLegalClient
              addressOptions={{
                requireAdress: true,
                requireFields: { street: true, streetNumber: true },
              }}
            />
          </form>
        </FormProvider>

        <Typography variant='h6' sx={{ mb: 2, mt: 3 }}>
          Informationen zur Beglaubigung
        </Typography>

        <SignatureCertificationLegalClientInformations
          data={editMemberData}
          setData={setEditMemberData}
        />

        <NavRow
          justify='right'
          handleBack={onClose}
          handleNext={onSubmit}
          backButtonText='Abbrechen'
          nextButtonText='Übernehmen'
          disableNext={
            !isValid ||
            !isSignatureCeritificationMemberDataValid(editMemberData)
          }
        />
      </DialogResponsive>

      <TableLegalClient
        legalClientArray={memberLegalClients}
        onRemoveClick={removeLegalClientFromStoreAndRelationships}
        onClickNew={onClickNew}
        onRowClick={onClickEdit}
        title='Unterzeichnende Personen'
      />

      <NavRow
        handleBack={handleBack}
        handleNext={handleNext}
        disableNext={legalClientArray.length === 0}
      />
    </Box>
  );
};
